import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants';
import {
  IFaceIdProfileCreation,
  IFaceIdProfileSearchingPayload,
  IFaceIdProfileUpdate,
} from '@/features/face-id-profiles/interfaces';
import {
  FaceIdProfileCreationService,
  FaceIdProfileService,
  FaceIdProfileUpdateService,
} from '@/features/face-id-profiles/services';

export const useFaceIdProfileQuery = (profileId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.faceIdProfile(profileId),
    queryFn: async () => {
      const response = await FaceIdProfileService.GetFaceIdProfile(profileId);
      return response.data;
    },
    enabled: !!profileId,
  });
};

export const useProfilesSearching = (payload: IFaceIdProfileSearchingPayload) => {
  return useQuery({
    queryKey: QUERY_KEYS.profilesSearching(payload),
    queryFn: () => FaceIdProfileService.ProfilesSearching(payload),
    placeholderData: keepPreviousData,
  });
};

export const useFaceIdProfileMutation = (profileId?: string) => {
  const queryClient = useQueryClient();

  const creationMutation = useMutation({
    mutationFn: async (profileData: IFaceIdProfileCreation) => {
      const response = await FaceIdProfileCreationService.CreateFaceIdProfile(profileData);
      return response.data;
    },
  });
  const updateMutation = useMutation({
    mutationFn: async (profileData: IFaceIdProfileUpdate) => {
      const response = await FaceIdProfileUpdateService.UpdateFaceIdProfile(profileData);
      return response.data;
    },
    onSuccess: () => {
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.faceIdProfile(profileId) });
      }
    },
  });

  return {
    creationMutation,
    updateMutation,
  };
};
